<template>
  <vs-popup
    :active="visible"
    title="포토 검색"
    @close="close"
    class="photo-picker-modal"
  >
    <!-- 상단 필터 -->
    <div class="flex items-center flex-wrap mb-4">
      <v-select
        class="mr-2"
        style="width: 140px"
        placeholder="기간(프리셋)"
        label="name"
        :reduce="e => e.code"
        :options="dateRanges"
        @input="setDatePreset"
      />
      <date-range-picker
        class="mr-2"
        ref="picker"
        :opens="opens"
        :locale="locale"
        :ranges="ranges"
        v-model="dateRange"
      />

      <v-select
        class="mr-2"
        style="width: 120px"
        placeholder="기자"
        label="nickname"
        :reduce="w => w.idx"
        v-model="searchVar.writer"
        :options="writer"
      />

      <vs-input
        class="mr-2"
        style="width: 220px"
        placeholder="제목/키워드"
        v-model="searchVar.searchText"
        @keyup.enter="searchPhotoList()"
      />

      <vs-button icon="icon-search" icon-pack="feather" @click="searchPhotoList()">검색</vs-button>
    </div>

    <!-- 결과 요약 / 페이지네이션 -->
    <div class="flex items-center justify-between mb-3">
      <div>총 {{ numOfPhoto.toLocaleString() }}건</div>
      <div class="flex items-center">
        <vs-select
          class="mr-2"
          style="width: 100px"
          v-model="searchVar.itemsPerPage"
          :autocomplete="false"
        >
          <vs-select-item :key="pp" :value="pp" :text="pp + '개'" v-for="pp in [8, 12, 20, 40, 80]" />
        </vs-select>
        <vs-pagination :total="numOfPages" v-model="currentPage" />
      </div>
    </div>

    <!-- 썸네일 그리드 -->
    <div class="grid grid-cols-4 gap-3" style="max-height: 60vh; overflow:auto;">

        <div
            class="photo-card cursor-pointer hover:shadow-sm"
            v-for="(row, idx) in users"
            :key="row._key || (row.pno + '_' + idx)"
            @click="toggleSelect(row)"
        >
            <div class="check">
                <vs-checkbox v-model="selectedMap[row._key]" @click.stop />
            </div>
            <div class="thumb">
                <img :src="imagePath(row.simg || row.mimg)" loading="lazy" decoding="async" fetchpriority="low" alt="" />
            </div>
            <div class="meta">
                <div class="title line-clamp-2">{{ row.subject || '(제목 없음)' }}</div>
                <div
                    class="desc line-clamp-2"
                    v-tooltip="{
                        content: descTooltip(row),   // HTML 허용
                        placement: 'top',
                        classes: ['photo-tooltip'],
                        delay: { show: 150, hide: 0 },
                        html: true
                    }"
                >
                {{ descText(row) }}
            </div>
        </div>
    </div>
    </div>

    <!-- 하단 액션 -->
    <div class="flex justify-between items-center mt-4">
      <div>선택: {{ selectedList.length }}개</div>
      <div>
        <vs-button type="border" @click="close">닫기</vs-button>
        <vs-button
          class="ml-2"
          type="filled"
          color="primary"
          :disabled="isConfirmLocked || selectedList.length === 0"
          @click="confirm"
        >
          {{ isConfirmLocked ? `잠금 ${confirmRemainSec}s` : '본문에 삽입' }}
        </vs-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import vSelect from 'vue-select'
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/lib/vue-daterange-picker.min.css'
import moment from 'moment'
import { imageUrl } from '@/utils/imageUrl'

export default {
  name: 'PhotoPickerModal',
  components: { 'v-select': vSelect, DateRangePicker },
  props: {
    value: { type: Boolean, default: false },   // v-model
    multiple: { type: Boolean, default: true }, // 단일선택 모드로 쓰고싶으면 false
  },
  data () {
    return {
      visible: this.value,
      users: [],
      numOfPhoto: 0,
      numOfPages: 0,
      currentPage: 1,
      selectedMap: {},
      searchVar: {
        startRow: 0,
        itemsPerPage: 20,
        state: null,
        usedOption: 'article', // article_photo JOIN article — 서버가 이 값만 처리한다
        writer: null,
        searchText: '',
        startDate: moment().subtract(1, 'year'),
        endDate: moment(),
        isCount: false,
      },
      isConfirmLocked: false,
      lockCountdown: 0,
      lockInterval: null,
      lockTimer: null,
      // 공통 스토어에서 받는 값들
      writer: this.$store?.state?.writer || [],

      // 날짜 선택기
      dateRanges: [
        { code: '1 week', name: '1주' },
        { code: '1 month', name: '1개월' },
        { code: '3 month', name: '3개월' },
        { code: '1 year', name: '1년' },
      ],
      ranges: {
        '오늘': [moment(), moment()],
        '1주일': [moment().subtract(1, 'week'), moment()],
        '한달': [moment().subtract(1, 'month'), moment()],
        '이번 달': [moment().startOf('month'), moment().endOf('month')],
        '지난 달': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
      },
      opens: 'center',
      locale: {
        direction: 'ltr',
        format: 'YYYY-MM-DD',
        separator: ' ~ ',
        applyLabel: '적용',
        cancelLabel: '취소',
        weekLabel: 'W',
        daysOfWeek: ['일','월','화','수','목','금','토'],
        monthNames: ['1월','2월','3월','4월','5월','6월','7월','8월','9월','10월','11월','12월'],
        firstDay: 0,
        showISOWeekNumbers: true
      },
      dateRange: {
        startDate: moment().subtract(1, 'year'),
        endDate: moment(),
      },
    }
  },
  watch: {
    value (v) {
      this.visible = v
      if (v) {
        this.resetAndLoad()
      }
    },
    visible (v) {
      this.$emit('input', v)
    },
    currentPage () {
      this.searchVar.startRow = (this.currentPage - 1) * this.searchVar.itemsPerPage
      this.getPhotoList()
    },
    'searchVar.itemsPerPage' () {
      this.currentPage = 1
      this.searchVar.startRow = 0
      this.getPhotoList()
    }
  },
  computed: {
    selectedList () {
      return this.users.filter(u => this.selectedMap[u._key])
    },
    confirmRemainSec () {
      const ms = this.confirmLockedUntil - Date.now()
      return ms > 0 ? Math.ceil(ms / 1000) : 0
    },
  },
  beforeDestroy () {
    if (this.lockInterval) { clearInterval(this.lockInterval); this.lockInterval = null }
    if (this.lockTimer) { clearTimeout(this.lockTimer); this.lockTimer = null }
  },
  methods: {
    descText (row) {
        // 설명으로 쓸 수 있는 후보 필드들: contents | desc | description | caption
        const raw = row?.contents || row?.desc || row?.description || row?.caption || ''
        // HTML 제거 + 공백 정리
        return String(raw).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
    },
    // 툴팁용(HTML 허용, 개행/긴문장 가독성)
    descTooltip (row) {
        const txt = this.descText(row)
        if (!txt) return '(설명 없음)'
        // 80자마다 소프트 브레이크, 원문 개행을 <br>로
        const withBreaks = txt.replace(/\n/g, '<br>')
        return withBreaks.replace(/(.{80})/g, '$1<wbr>')
    },
    setDatePreset (code) {
      if (!code) return
      const [num, unit] = code.split(' ')
      this.dateRange = { startDate: moment().subtract(+num, unit), endDate: moment() }
      this.searchVar.startDate = this.dateRange.startDate
      this.searchVar.endDate = this.dateRange.endDate
    },
    // 🔴 예전에는 `https://cdn.jasm.co.kr/photo/` 를 **하드코딩**했다.
    //    그 도메인은 운영용이고 DNS 가 아직 안 붙어 있어 **썸네일이 전부 엑박**이었고,
    //    같은 값이 본문 <img src> 로도 들어가 삽입한 사진까지 깨졌다(2026-09-29).
    //    경로 규칙은 utils/imageUrl.js 한 곳에 모여 있다 — 여기서 다시 만들지 않는다.
    //    목록이 주는 값은 `article_photo.filepath`(= `YYYYMM/파일`) 이고,
    //    imageUrl 이 `/news/photo/YYYYMM/파일` 로 바꿔 준다. 도메인을 붙이지 않는 게
    //    중요하다 — CMS 의 /news/ 라우트가 로컬 → CDN → 옛 사이트 순으로 찾아 준다.
    imagePath (img) {
      return imageUrl(img)
    },
    resetAndLoad () {
      // 날짜 동기화
      this.searchVar.startDate = this.dateRange.startDate
      this.searchVar.endDate = this.dateRange.endDate
      // 페이징/선택 초기화
      this.currentPage = 1
      this.searchVar.startRow = 0
      this.selectedMap = {}
      // 로드
      this.getPhotoList()
    },
    searchPhotoList () {
      this.currentPage = 1
      this.searchVar.startRow = 0
      this.getPhotoList()
    },
    getPhotoList () {
      const data = { ...this.searchVar, isCount: false }
      const thisIns = this
      this.$vs.loading({ scale: 1.5 })
      this.$http.post('/api/getNewsList/photo', data)
        .then(function (response) {
          // 응답 형태가 [ [ {photoCount} ], [list] ] 또는 [ {photoCount}, [list] ] 로 올 때가 있어 안전하게 처리
          let count = 0
          let list = []
          if (Array.isArray(response.data?.[0]) && response.data?.[0]?.[0]?.photoCount != null) {
            count = +response.data[0][0].photoCount
            list = response.data[1] || []
          } else if (response.data?.[0]?.photoCount != null) {
            count = +response.data[0].photoCount
            list = response.data[1] || []
          } else if (Array.isArray(response.data)) {
            // 최악의 경우 리스트만 오는 백엔드도 대비
            list = response.data
            count = list.length
          }
        //   thisIns.users = list
        
        // 🔒 기사이미지는 pno가 겹치거나 undefined일 수 있으므로 안전한 키를 생성
        // thisIns.users = (list || []).map((it, i) => {
        //     const k = it.pno || it.ano || it.id || it.mid || it.uid || it.mimg || (`idx_${i}`)
        //     return { ...it, _key: String(k) }
        // })
          thisIns.users = (list || []).map((it, i) => {
            // 1) 가능한 원천 키 후보를 모으고
            const baseKey =
              it._key ||
              it.pno || it.ano || it.id || it.mid || it.uid ||
              it.simg || it.mimg || 'row';
            // 2) 동일 파일/ID가 반복되어도 유니크하도록 인덱스를 붙여 강제 고유화
            const uniqueKey = `${baseKey}::${i}`;
            const subject = it.subject || '';
            const desc = String(it.contents || it.desc || it.description || it.caption || '')
              .replace(/<[^>]*>/g, ' ')
              .replace(/\s+/g, ' ')
              .trim();

            return Object.freeze({
              _key: String(uniqueKey),     // ✅ v-for에서 사용할 절대 유일 키
              mimg: it.mimg,
              simg: it.simg,
              subject,
              desc,
            });
          })
          thisIns.numOfPhoto = count
          thisIns.numOfPages = Math.max(1, Math.ceil(count / thisIns.searchVar.itemsPerPage))
          thisIns.$vs.loading.close()
        })
        .catch(function (error) {
          const msg = error && error.toString ? error.toString() : error
          if (msg.includes('401')) {
            alert('로그인 해주세요!')
            thisIns.$router.push({ path: '/pages/login', query: { to: thisIns.$route?.fullPath } })
          } else {
            thisIns.$vs.notify({ title: 'Error', text: msg, color: 'danger', iconPack: 'feather', icon: 'icon-alert-circle' })
          }
          thisIns.$vs.loading.close()
        })
    },
    toggleSelect (row) {
      if (!this.multiple) {
        this.selectedMap = {}
      }
      const k = row._key
      this.$set(this.selectedMap, k, !this.selectedMap[k])
    },
    confirm () {
      // 5초 잠금 가드
      if (this.isConfirmLocked) return
      this.confirmLockedUntil = Date.now() + 5000
      if (!this.confirmTimer) {
        this.confirmTimer = setInterval(() => {
          if (!this.isConfirmLocked) { clearInterval(this.confirmTimer); this.confirmTimer = null }
          this.$forceUpdate() // 남은 초 갱신 UI
        }, 250)
      }
      const payload = this.selectedList.map(r => ({
        pno: r.pno,
        mimg: r.mimg,
        simg: r.simg,
        subject: r.subject,
        contents: r.desc,
        src: this.imagePath(r.simg || r.mimg), // 완전한 URL
      }))
      this.$emit('confirm', payload)
      this.close()
    },
    close () {
      this.visible = false
    }
  },
  created () {
    // 최초 열렸을 때만 로드 (부모가 v-model로 열면 watch에서 처리)
    if (this.visible) this.resetAndLoad()
  }
}
</script>

<style scoped>
.photo-picker-modal .line-clamp-2 {
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.photo-picker-modal .desc {
   margin-top: .25rem;
   font-size: 12px;
   color: #666;
}
.grid { display: grid; }
.grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.gap-3 { grid-gap: .75rem; }
.mb-3 { margin-bottom: .75rem; }
.mb-4 { margin-bottom: 1rem; }
.mr-2 { margin-right: .5rem; }
.ml-1 { margin-left: .25rem; }
.ml-2 { margin-left: .5rem; }

/* 카드 레이아웃 */
.photo-card {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 8px;
  border: 1px solid #eee;
  border-radius: 10px;
  box-sizing: border-box;
  height: 260px;              /* 고정 높이: 썸네일+제목+설명 2줄 기준 */
  background: #fff;
}
.photo-card .check {
  position: absolute;
  top: 6px; left: 6px;
  z-index: 2;                 /* 썸네일 위에 항상 보이게 */
}
.photo-card .thumb {
  width: 100%;
  aspect-ratio: 16 / 10;      /* 비율 고정 (지원 안 되는 브라우저면 fallback로 min-height 사용 권장) */
  background: #f6f6f8;
  border-radius: 8px;
  overflow: hidden;
}
.photo-card .thumb img {
  width: 100%; height: 100%; object-fit: cover; display:block;
}
.photo-card .meta {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-height: 64px;           /* 제목+설명 영역 최소높이 확보 */
}
.photo-card .title {
  font-size: 13px;
  font-weight: 600;
}
.photo-card .desc {
  font-size: 12px;
  color: #666;
}
</style>

<!-- Vuesax 툴팁이 모달 스크롤에 가려지지 않게 z-index 보정 -->
<style>
.tooltip.photo-tooltip { z-index: 100000 !important; }
.tooltip.photo-tooltip .tooltip-inner {
  max-width: 520px;
  white-space: normal;
  word-break: break-word;
}
</style>